/**
 * Contextual Modify tab — one-shot action backends for Families (Stairs),
 * Datum (Grids/Levels), Annotations (Dimensions/Text) and Groups.
 * Composed by useContextualActions().
 */
import { useCallback } from 'react';
import { useBIMStore } from '../../state/useBIMStore';
import { useAppStore } from '../../state/store';
import { notify } from '../../utils/notify';
import {
    getSelectedElements,
    getSelectedIds,
    elementWorldBounds,
} from '../../utils/contextualModelUtils';
import { StairElement } from '../../core/types';
import i18n from '../../i18n';

/* ============================ Stairs & Railings ============================ */

export function useStairContextualActions() {
    /** Flip Direction: reverse the stair run walk order (Up ↔ Down). */
    const flipStairDirection = useCallback(() => {
        const stairs = getSelectedElements().filter(e => e.category === 'Stairs') as StairElement[];
        if (stairs.length === 0) {
            notify(i18n.t('tools.notify.selectAStairToFlipIts'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        stairs.forEach(stair => {
            const g = stair.geometryData as any;
            const flipList = <T extends { path?: number[][] }>(arr?: T[]) =>
                Array.isArray(arr) ? arr.map(run => run.path ? { ...run, path: [...run.path].reverse() } : run).reverse() : arr;
            bim.updateElement(stair.id, {
                parameters: { ...stair.parameters, 'Up/Down Flipped': !(stair.parameters['Up/Down Flipped'] === true) },
                geometryData: {
                    ...g,
                    runs: flipList(g.runs),
                    components: flipList(g.components),
                },
            } as any);
        });
        notify(i18n.t('tools.notify.stairDirectionFlipped'), 'success');
    }, []);

    /**
     * Reset Stair: drop manual run/landing overrides back to the family
     * defaults and re-solve risers/treads across the level distance.
     */
    const resetStair = useCallback(() => {
        const stairs = getSelectedElements().filter(e => e.category === 'Stairs') as StairElement[];
        if (stairs.length === 0) {
            notify(i18n.t('tools.notify.selectAStairToReset'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        stairs.forEach(stair => {
            const g = stair.geometryData as any;
            const base = bim.project.levels.get(g.baseLevelId);
            const top = bim.project.levels.get(g.topLevelId);
            const totalHeight = Math.abs((top?.elevation ?? 3000) - (base?.elevation ?? 0)) / 1000;
            const desiredRiser = 0.18; // family default (m)
            const desiredTread = 0.28;
            const count = Math.max(1, Math.round((totalHeight || desiredRiser) / desiredRiser));
            bim.updateElement(stair.id, {
                geometryData: {
                    ...g,
                    desiredRiserHeight: desiredRiser,
                    desiredTreadDepth: desiredTread,
                    actualRiserHeight: (totalHeight || desiredRiser) / count,
                    actualTreadDepth: desiredTread,
                    riserCount: count,
                    runs: undefined,
                    landings: undefined,
                    components: undefined,
                },
            } as any);
        });
        notify(i18n.t('tools.notify.stairResetToFamilyDefaults'), 'success');
    }, []);

    return { flipStairDirection, resetStair };
}

/* ============================ Grids & Levels ============================ */

/** Union AABB of every model element (approximates "model extents"). */
function modelExtents(): { min: [number, number]; max: [number, number]; span: [number, number] } | null {
    const bim = useBIMStore.getState();
    const box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    Array.from(bim.project.elements.values()).forEach(el => {
        const b = elementWorldBounds(el);
        if (!b) return;
        box.minX = Math.min(box.minX, b.min.x);
        box.minY = Math.min(box.minY, b.min.y);
        box.maxX = Math.max(box.maxX, b.max.x);
        box.maxY = Math.max(box.maxY, b.max.y);
    });
    if (!Number.isFinite(box.minX) || box.maxX <= box.minX) return null;
    const pad = 2;
    return {
        min: [box.minX - pad, box.minY - pad],
        max: [box.maxX + pad, box.maxY + pad],
        span: [box.maxX - box.minX + pad * 2, box.maxY - box.minY + pad * 2],
    };
}

/** Selected grid ids (grids live in project.grids, not in elements). */
function selectedGridIds(): string[] {
    const bim = useBIMStore.getState();
    return getSelectedIds().filter(id => bim.project.grids.has(id));
}

/** Selected level ids (project.levels is the datum map). */
function selectedLevelIds(): string[] {
    const bim = useBIMStore.getState();
    return getSelectedIds().filter(id => bim.project.levels.has(id));
}

export function useDatumContextualActions() {
    /** Maximize Extents: stretch each selected grid/level to model extents. */
    const maximizeExtents = useCallback(() => {
        const bim = useBIMStore.getState();
        const extents = modelExtents();
        if (!extents) {
            notify(i18n.t('tools.notify.modelIsEmptyNothingToMaximize'), 'warning');
            return;
        }
        let touched = 0;
        selectedGridIds().forEach(id => {
            const grid = bim.project.grids.get(id)!;
            const dirX = grid.end[0] - grid.start[0];
            const dirY = grid.end[1] - grid.start[1];
            const len = Math.hypot(dirX, dirY) || 1;
            // Axis-aligned grids snap to full extents; angled ones grow along
            // their own direction from the center.
            const cx = (grid.start[0] + grid.end[0]) / 2;
            const cy = (grid.start[1] + grid.end[1]) / 2;
            if (Math.abs(dirY) < 1e-6) { // horizontal grid → full X span
                bim.updateGrid(id, { start: [extents.min[0], grid.start[1]], end: [extents.max[0], grid.end[1]] });
            } else if (Math.abs(dirX) < 1e-6) { // vertical grid → full Y span
                bim.updateGrid(id, { start: [grid.start[0], extents.min[1]], end: [grid.end[0], extents.max[1]] });
            } else {
                const half = Math.max(extents.span[0], extents.span[1]) / 2;
                const ux = dirX / len, uy = dirY / len;
                bim.updateGrid(id, {
                    start: [cx - ux * half, cy - uy * half],
                    end: [cx + ux * half, cy + uy * half],
                });
            }
            touched++;
        });
        selectedLevelIds().forEach(id => {
            bim.updateLevel(id, { extents2D: { min: extents.min, max: extents.max } });
            touched++;
        });
        if (touched === 0) {
            notify(i18n.t('tools.notify.selectAGridOrLevelTo'), 'warning');
            return;
        }
        notify(i18n.t('tools.notify.extentsMaximized', { count: touched }), 'success');
    }, []);

    /**
     * Propagate Extents: copy the extents of the selected datum(s) onto every
     * other datum of the same family (BIM propagates 2D extents to all views;
     * here datums are global, so the extent is normalized across parallels).
     */
    const propagateExtents = useCallback(() => {
        const bim = useBIMStore.getState();
        const gridIds = selectedGridIds();
        const levelIds = selectedLevelIds();
        if (gridIds.length === 0 && levelIds.length === 0) {
            notify(i18n.t('tools.notify.selectAGridOrLevelTo2'), 'warning');
            return;
        }
        if (levelIds.length > 0) {
            const source = bim.project.levels.get(levelIds[0]);
            if (source?.extents2D) {
                let n = 0;
                bim.project.levels.forEach((lvl) => {
                    if (lvl.id === source.id) return;
                    bim.updateLevel(lvl.id, { extents2D: { ...source.extents2D! } });
                    n++;
                });
                notify(i18n.t('tools.notify.levelExtentsPropagated', { count: n }), 'success');
            } else {
                notify(i18n.t('tools.notify.selectedLevelUsesAutomaticExtentsMaximize'), 'warning');
            }
        }
        if (gridIds.length > 0) {
            // Propagate span lengths to all grids of matching orientation.
            let n = 0;
            gridIds.forEach(srcId => {
                const src = bim.project.grids.get(srcId)!;
                const srcLen = Math.hypot(src.end[0] - src.start[0], src.end[1] - src.start[1]);
                bim.project.grids.forEach(grid => {
                    if (grid.id === src.id) return;
                    const gx = grid.end[0] - grid.start[0];
                    const gy = grid.end[1] - grid.start[1];
                    const sameOrientation =
                        (Math.abs(gy) < 1e-6 && Math.abs(src.end[1] - src.start[1]) < 1e-6) ||
                        (Math.abs(gx) < 1e-6 && Math.abs(src.end[0] - src.start[0]) < 1e-6);
                    if (!sameOrientation) return;
                    const len = Math.hypot(gx, gy) || 1;
                    const cx = (grid.start[0] + grid.end[0]) / 2;
                    const cy = (grid.start[1] + grid.end[1]) / 2;
                    const ux = gx / len, uy = gy / len;
                    bim.updateGrid(grid.id, {
                        start: [cx - ux * srcLen / 2, cy - uy * srcLen / 2],
                        end: [cx + ux * srcLen / 2, cy + uy * srcLen / 2],
                    });
                    n++;
                });
            });
            notify(i18n.t('tools.notify.gridExtentsPropagated', { count: n }), 'success');
        }
    }, []);

    /** Reset to 3D Extents: restore automatic / creation-time extents. */
    const reset3DExtents = useCallback(() => {
        const bim = useBIMStore.getState();
        const gridIds = selectedGridIds();
        const levelIds = selectedLevelIds();
        if (gridIds.length === 0 && levelIds.length === 0) {
            notify(i18n.t('tools.notify.selectAGridOrLevelTo3'), 'warning');
            return;
        }
        let touched = 0;
        levelIds.forEach(id => { bim.updateLevel(id, { extents2D: undefined }); touched++; });
        gridIds.forEach(id => {
            const grid = bim.project.grids.get(id)!;
            if (grid.extents3D) {
                bim.updateGrid(id, { start: [...grid.extents3D.start] as [number, number], end: [...grid.extents3D.end] as [number, number] });
            }
            touched++;
        });
        notify(i18n.t('tools.notify.extentsReset', { count: touched }), 'success');
    }, []);

    return { maximizeExtents, propagateExtents, reset3DExtents };
}

/* ============================ Dimensions & Text ============================ */

export function useAnnotationContextualActions() {
    /** Equality Display (EQ): toggle numeric vs. EQ-notation on dimensions. */
    const dimensionEquality = useCallback(() => {
        const dims = getSelectedElements().filter(e => e.category === 'Dimensions');
        if (dims.length === 0) {
            notify(i18n.t('tools.notify.selectADimensionToChangeIts'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        const next = !(dims[0].parameters['Equality'] === true);
        dims.forEach(dim => {
            bim.updateElement(dim.id, {
                parameters: { ...dim.parameters, 'Equality': next },
            } as any);
        });
        notify(i18n.t('tools.notify.equalityDisplay', { state: next ? i18n.t('tools.notify.eqEnabled') : i18n.t('tools.notify.eqDisabled') }), 'success');
    }, []);

    /** Shared parameter writer for text notes. */
    const setTextParam = (key: string, value: unknown, successMsg: string) => {
        const notes = getSelectedElements().filter(e =>
            e.category === 'Text Notes' || e.category === 'ModelText' || e.category === 'Model Text');
        if (notes.length === 0) {
            notify(i18n.t('tools.notify.selectATextNoteFirst'), 'warning');
            return;
        }
        const bim = useBIMStore.getState();
        notes.forEach(note => {
            bim.updateElement(note.id, {
                geometryData: { ...(note as any).geometryData, [key]: value },
                parameters: { ...note.parameters, [key === 'leader' ? 'Leader' : 'Text Alignment']: value },
            } as any);
        });
        notify(successMsg, 'success');
    };

    const textLeaderLeft = useCallback(() => setTextParam('leader', 'Left', i18n.t('tools.notify.leftLeaderAdded')), []);
    const textLeaderRight = useCallback(() => setTextParam('leader', 'Right', i18n.t('tools.notify.rightLeaderAdded')), []);
    const textLeaderNone = useCallback(() => setTextParam('leader', null, i18n.t('tools.notify.leaderRemoved')), []);
    const textAlignLeft = useCallback(() => setTextParam('alignment', 'Left', i18n.t('tools.notify.textAlignedLeft')), []);
    const textAlignCenter = useCallback(() => setTextParam('alignment', 'Center', i18n.t('tools.notify.textAlignedCenter')), []);
    const textAlignRight = useCallback(() => setTextParam('alignment', 'Right', i18n.t('tools.notify.textAlignedRight')), []);

    return {
        dimensionEquality,
        textLeaderLeft, textLeaderRight, textLeaderNone,
        textAlignLeft, textAlignCenter, textAlignRight,
    };
}

/* ============================ Model Groups ============================ */

export function useGroupContextualActions() {
    /** Edit Group: enter the group by selecting its members (BIM edits the
     *  group in-place with a green halo; here membership becomes the working
     *  selection so modify tools act on the group contents). */
    const editGroup = useCallback(() => {
        const groups = getSelectedElements().filter(e =>
            e.category === 'Model Groups' || e.category === 'ModelGroups');
        if (groups.length === 0) {
            notify(i18n.t('tools.notify.selectAModelGroupToEdit'), 'warning');
            return;
        }
        const members = (
            ((groups[0] as any).geometryData?.memberIds ?? (groups[0] as any).memberIds ?? []) as string[]
        );
        if (members.length === 0) {
            notify(i18n.t('tools.notify.thisGroupHasNoMembers'), 'info');
            return;
        }
        const bim = useBIMStore.getState();
        bim.setSelection([groups[0].id, ...members]);
        useAppStore.getState().select([groups[0].id, ...members]);
        notify(i18n.t('tools.notify.groupEditing', { name: groups[0].name ?? groups[0].type, count: members.length }), 'success');
    }, []);

    /** Ungroup: dissolve the group, members stay independent. */
    const ungroup = useCallback(() => {
        const bim = useBIMStore.getState();
        const groups = getSelectedElements().filter(e =>
            e.category === 'Model Groups' || e.category === 'ModelGroups');
        if (groups.length === 0) {
            notify(i18n.t('tools.notify.selectAModelGroupToUngroup'), 'warning');
            return;
        }
        groups.forEach(g => bim.removeElement(g.id));
        notify(i18n.t('tools.notify.ungrouped', { count: groups.length }), 'success');
    }, []);

    /** Convert Group to Link: mark the group as an external link container. */
    const linkGroup = useCallback(() => {
        const bim = useBIMStore.getState();
        const groups = getSelectedElements().filter(e =>
            e.category === 'Model Groups' || e.category === 'ModelGroups');
        if (groups.length === 0) {
            notify(i18n.t('tools.notify.selectAModelGroupToConvert'), 'warning');
            return;
        }
        const g = groups[0];
        bim.updateElement(g.id, {
            type: `Group Link: ${g.name ?? 'Group'}`,
            parameters: { ...g.parameters, 'Is Group': true, 'Is Link': true },
        } as any);
        notify(i18n.t('tools.notify.groupConvertedToAGroupType'), 'success');
    }, []);

    return { editGroup, ungroup, linkGroup };
}

/* ============================ Selection Box ============================ */

/**
 * Selection Box (View panel): isolate the active 3D view by clipping to the
 * selection AABB (ViewProcessor already consumes view.sectionBox planes).
 */
export function useSelectionBoxAction() {
    const selectionBox = useCallback(() => {
        const bim = useBIMStore.getState();
        const els = getSelectedElements();
        if (els.length === 0) {
            notify(i18n.t('tools.notify.selectElementSToBuildA'), 'warning');
            return;
        }
        const view = bim.project.views.get(bim.project.activeViewId);
        if (!view) {
            notify(i18n.t('tools.notify.noActiveView'), 'warning');
            return;
        }
        // Toggle off when a selection box already matches this selection.
        if (view.sectionBox) {
            bim.updateView(view.id, { sectionBox: undefined } as any);
            notify(i18n.t('tools.notify.selectionBoxCleared'), 'info');
            return;
        }
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        els.forEach(el => {
            const b = elementWorldBounds(el);
            if (!b) return;
            minX = Math.min(minX, b.min.x); minY = Math.min(minY, b.min.y); minZ = Math.min(minZ, b.min.z);
            maxX = Math.max(maxX, b.max.x); maxY = Math.max(maxY, b.max.y); maxZ = Math.max(maxZ, b.max.z);
        });
        if (!Number.isFinite(minX)) {
            notify(i18n.t('tools.notify.selectedElementsHaveNoMeasurableGeometry'), 'warning');
            return;
        }
        const pad = 0.5;
        bim.updateView(view.id, {
            sectionBox: {
                min: [minX - pad, minY - pad, minZ - pad],
                max: [maxX + pad, maxY + pad, maxZ + pad],
            },
        });
        notify(i18n.t('tools.notify.selectionBoxActiveViewClippedTo'), 'success');
    }, []);
    return { selectionBox };
}
